import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HomeIcon from '@mui/icons-material/Home';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import LayersIcon from '@mui/icons-material/Layers';
import SendIcon from '@mui/icons-material/Send';
import SyncIcon from '@mui/icons-material/Sync';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BusinessIcon from '@mui/icons-material/Business';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PwaInstallMenuItem from '../PwaInstallMenuItem';

const LayoutMobileMenu = ({
  mode,
  toggleMode,
  defaultOpen = false,
  autoOpenToken = null,
  handleCopyUrl,
  goStory,
  goHome,
  goEnrollmentStatus,
  goSelections,
  goVmtStaging,
  goBulkSend,
  goBuySells,
  goBillingReconciliation,
  goAppsheetAccounts,
  goAppsheetOrderItems,
  goReports,
  handleForceSync,
  handleAbortSync,
  syncSpinning,
  isAbortPending,
  isSyncRequestPending,
  goSettings,
  user,
  logout,
  GoogleDomainSignInButton,
}) => {
  const [menuOpen, setMenuOpen] = useState(Boolean(defaultOpen));
  const lastAutoOpenTokenRef = useRef(null);

  useEffect(() => {
    if (!defaultOpen) return;
    const token = autoOpenToken || 'home-default';
    if (lastAutoOpenTokenRef.current === token) return;
    lastAutoOpenTokenRef.current = token;
    setMenuOpen(true);
  }, [autoOpenToken, defaultOpen]);

  const handleMenuOpen = () => setMenuOpen(true);
  const handleMenuClose = () => setMenuOpen(false);
  const sectionLabelSx = { lineHeight: 1.8, bgcolor: 'transparent', color: 'text.secondary', fontSize: 12 };

  return (
    <>
      <IconButton color="inherit" edge="start" onClick={handleMenuOpen} aria-label="open navigation menu">
        <MenuIcon />
      </IconButton>
      <Menu
        anchorReference="anchorPosition"
        anchorPosition={{ top: 0, left: 0 }}
        open={menuOpen}
        hideBackdrop={false}
        onClose={handleMenuClose}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(10, 14, 22, 0.35)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: '86vw', sm: 320, md: 360 },
            maxWidth: '90vw',
            height: 'calc(100vh - 32px)',
            maxHeight: 'calc(100vh - 32px)',
            borderRadius: 0,
            top: '0 !important',
            left: '0 !important',
            transform: 'none !important',
          },
        }}
        MenuListProps={{
          sx: {
            py: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          },
        }}
      >
        <ListSubheader disableSticky sx={sectionLabelSx}>
          Application
        </ListSubheader>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goHome();
          }}
        >
          <ListItemIcon>
            <HomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Home" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goStory();
          }}
        >
          <ListItemIcon>
            <AutoAwesomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Daily Digest" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goBuySells();
          }}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Buy/Sells" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goBillingReconciliation();
          }}
        >
          <ListItemIcon>
            <AttachMoneyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Billing Reconciliation" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goReports();
          }}
        >
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Report Dashboard" />
        </MenuItem>
        <Divider />

        <ListSubheader disableSticky sx={sectionLabelSx}>
          DPE
        </ListSubheader>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goEnrollmentStatus();
          }}
        >
          <ListItemIcon>
            <FactCheckIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Enrollment Status" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goSelections();
          }}
        >
          <ListItemIcon>
            <PlaylistAddCheckIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Selections" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goVmtStaging();
          }}
        >
          <ListItemIcon>
            <LayersIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="VMT Staging" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goBulkSend();
          }}
        >
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Bulk Send" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (syncSpinning) {
              handleAbortSync();
            } else {
              handleForceSync();
            }
          }}
          disabled={syncSpinning ? isAbortPending : isSyncRequestPending}
        >
          <ListItemIcon>
            <SyncIcon fontSize="small" sx={{ animation: syncSpinning ? 'spin 2s linear infinite' : 'none' }} />
          </ListItemIcon>
          <ListItemText
            primary={
              syncSpinning
                ? isAbortPending
                  ? 'Aborting...'
                  : 'Abort Sync'
                : isSyncRequestPending
                  ? 'Syncing...'
                  : 'Force Sync'
            }
          />
        </MenuItem>
        <Divider />

        <ListSubheader disableSticky sx={sectionLabelSx}>
          Appsheet
        </ListSubheader>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goAppsheetAccounts();
          }}
        >
          <ListItemIcon>
            <BusinessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Accounts" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            goAppsheetOrderItems();
          }}
        >
          <ListItemIcon>
            <ShoppingCartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="OrderItems" />
        </MenuItem>
        <Divider />

        <ListSubheader disableSticky sx={sectionLabelSx}>
          Actions
        </ListSubheader>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            toggleMode();
          }}
        >
          <ListItemIcon>
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText primary="Theme" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            void handleCopyUrl();
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Copy URL" />
        </MenuItem>
        <PwaInstallMenuItem onAfterClick={handleMenuClose} />

        <Box
          component="li"
          sx={{
            listStyle: 'none',
            mt: 'auto',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <Divider />
          <MenuItem
            component="div"
            onClick={() => {
              handleMenuClose();
              goSettings();
            }}
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>
          {user ? (
            <>
              <MenuItem component="div" disabled sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <Avatar alt={user.name} src={user.picture} sx={{ width: 28, height: 28 }} />
                </ListItemIcon>
                <ListItemText primary={user.email} />
              </MenuItem>
              <MenuItem
                component="div"
                onClick={() => {
                  handleMenuClose();
                  logout();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Logout" />
              </MenuItem>
            </>
          ) : (
            <Box sx={{ px: 2, py: 1 }}>
              <GoogleDomainSignInButton />
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default LayoutMobileMenu;
